import React, { useState } from 'react';
import { Button, Card, Descriptions, Typography, Alert, Modal, Space, Divider, Tag, Image, Row, Col, Spin } from 'antd';
import { 
  CarOutlined,
  UserOutlined,
  CameraOutlined,
  WarningOutlined,
  FileTextOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  SendOutlined
} from '@ant-design/icons';
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { 
  DamageLocationLabels,
  DamageLocation 
} from '../../../redux/reducers/damageLocationsSlice';
import { 
  getCompletedRequiredPhotosCount, 
  PhotoTypes,
  DisplayPhoto 
} from '../../../redux/reducers/vehiclePhotosSlice';
import Api from '../../../services/api';
import { CreateIncidentRequest } from '../../../types/api.types';
import { setIncidentId, setIsFinalized } from '../../../redux/reducers/incidentDetailsSlice';

const { Title, Text, Paragraph } = Typography;

interface ReviewSubmitStepProps {
  onNext: () => void;
  onPrevious: () => void;
  onComplete: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

const ReviewSubmitStep: React.FC<ReviewSubmitStepProps> = ({ 
  onPrevious,
  onComplete
}) => {
  const dispatch = useAppDispatch();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Get all state data
  const vehicle = useAppSelector((state) => state.vehicle);
  const operatorInfo = useAppSelector((state) => state.operatorInfo);
  const incidentDetails = useAppSelector((state) => state.incidentDetails);
  const damageLocations = useAppSelector((state) => state.damageLocations);
  const damagedParts = useAppSelector((state) => state.damagedParts);
  const vehiclePhotos = useAppSelector((state) => state.vehiclePhotos);
  // const auth = useAppSelector((state) => state.auth);

  const completedPhotos = getCompletedRequiredPhotosCount(vehiclePhotos.photoMap);
  const totalDamagedParts = Object.values(damagedParts.selectedParts).flat().length;

  const buildIncidentRequest = (): CreateIncidentRequest => {
    // Collect all selected damage locations as object
    const damageLocationsObj: Record<string, unknown> = {};
    if (!damageLocations.noDamage) {
      damageLocations.selectedLocations.forEach((location: DamageLocation, index: number) => {
        damageLocationsObj[index.toString()] = location;
      });
    }
    
    // Collect all selected parts as object
    const damagedPartsObj: Record<string, unknown> = {};
    let partIndex = 0;
    Object.values(damagedParts.selectedParts).forEach((parts) => {
      (parts as string[]).forEach((part) => {
        damagedPartsObj[partIndex.toString()] = part;
        partIndex++;
      });
    });

    return {
      IncidentDate: incidentDetails.incidentDate.toISOString(),
      OperatorInjured: operatorInfo.operatorInjured ?? false,
      OperatorId: operatorInfo.selectedOperator?.operatorId || '',
      VehicleId: vehicle.vehicle?.vehicleId || 0,
      Vin: vehicle.vehicle?.vin || '',
      VehicleNumber: vehicle.vehicle?.vehicleNumber || '',
      DamagedLocations: damageLocationsObj,
      DamagedParts: damagedPartsObj,
      IncidentId: 0, // New incident
      Finalize: true,
      AdditionalInformation: vehicle.additionalInformation || '',
      IsDrivable: vehicle.vehicleIsDrivable ?? true,
      Mileage: vehicle.currentMileage,
      AccidentLocationDescription: incidentDetails.accidentLocationDescription,
      AdditionalSafetyNotes: incidentDetails.additionalSafetyNotes,
      IncidentType: incidentDetails.incidentType
    };
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    
    try {
      const incidentData = buildIncidentRequest();
      
      // Submit to API
      const response = await Api.createIncident(incidentData);
      
      // Save incident ID
      dispatch(setIncidentId(response.id));
      dispatch(setIsFinalized(true));
      
      // Show success modal
      Modal.success({
        title: 'Incident Submitted Successfully',
        content: (
          <div>
            <p>Your incident report has been submitted successfully.</p>
            <p><strong>Incident ID:</strong> {response.id}</p>
            <p>The incident will be reviewed by the appropriate team.</p>
          </div>
        ),
        onOk: onComplete,
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to submit incident. Please try again.');
      console.error('Failed to submit incident:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="step-container">
      <Title level={3}>Review & Submit</Title>
      <Text type="secondary">Please review all information carefully before submitting</Text>

      {error && (
        <Alert
          message="Submission Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginTop: 20, marginBottom: 20 }}
        />
      )}

      <div style={{ marginTop: 20 }}>
        {/* Vehicle Information */}
        <Card 
          title={<><CarOutlined /> Vehicle Information</>} 
          style={{ marginBottom: 16 }}
        >
          <Descriptions column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}>
            <Descriptions.Item label="Vehicle Number">
              <strong>{vehicle.vehicle?.vehicleNumber || 'N/A'}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="VIN">
              {vehicle.vehicle?.vin || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Make/Model">
              {`${vehicle.vehicle?.make || ''} ${vehicle.vehicle?.model || ''} ${vehicle.vehicle?.year || ''}`}
            </Descriptions.Item>
            <Descriptions.Item label="Color">
              {vehicle.vehicle?.color || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Current Mileage">
              {vehicle.currentMileage ? `${vehicle.currentMileage.toLocaleString()} miles` : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Drivable">
              <Tag color={vehicle.vehicleIsDrivable ? 'green' : 'red'}>
                {vehicle.vehicleIsDrivable ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Operator Information */}
        <Card 
          title={<><UserOutlined /> Operator Information</>} 
          style={{ marginBottom: 16 }}
        >
          <Descriptions column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}>
            <Descriptions.Item label="Operator Name">
              <strong>
                {operatorInfo.selectedOperator ? 
                  `${operatorInfo.selectedOperator.firstName} ${operatorInfo.selectedOperator.lastName}` : 'N/A'}
              </strong>
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {operatorInfo.selectedOperator?.email || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Injury Status">
              <Tag color={operatorInfo.operatorInjured ? 'red' : 'green'}>
                {operatorInfo.operatorInjured ? 'Injury Reported' : 'No Injury'}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Photos Summary */}
        <Card 
          title={<><CameraOutlined /> Photos</>} 
          style={{ marginBottom: 16 }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text>Required Photos: </Text>
              <Tag color={completedPhotos >= 9 ? 'green' : 'orange'}>
                {completedPhotos} of 9 captured
              </Tag>
            </div>
            {Object.entries(vehiclePhotos.photoMap).length > 0 && (
              <Row gutter={[8, 8]}>
                {Object.entries(vehiclePhotos.photoMap).slice(0, 6).map(([type, photo]) => (
                  <Col key={type} span={4}>
                    <Image
                      src={(photo as DisplayPhoto).uri}
                      alt={PhotoTypes[parseInt(type)]}
                      style={{ width: '100%', height: 60, objectFit: 'cover' }}
                      preview={{
                        mask: <Text style={{ fontSize: 10 }}>{PhotoTypes[parseInt(type)]}</Text>
                      }}
                    />
                  </Col>
                ))}
                {Object.entries(vehiclePhotos.photoMap).length > 6 && (
                  <Col span={4}>
                    <div style={{ 
                      width: '100%', 
                      height: 60, 
                      background: '#f0f2f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Text type="secondary">+{Object.entries(vehiclePhotos.photoMap).length - 6} more</Text>
                    </div>
                  </Col>
                )}
              </Row>
            )}
          </Space>
        </Card>

        {/* Damage Assessment */}
        <Card 
          title={<><WarningOutlined /> Damage Assessment</>} 
          style={{ marginBottom: 16 }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            {damageLocations.noDamage ? (
              <Tag color="green">No Visible Damage</Tag>
            ) : (
              <>
                <div>
                  <Text strong>Damage Locations ({damageLocations.selectedLocations.length}):</Text>
                  <div style={{ marginTop: 8 }}>
                    {damageLocations.selectedLocations.map((location: DamageLocation) => (
                      <Tag key={location} color="orange" style={{ marginBottom: 4 }}>
                        {DamageLocationLabels[location]}
                      </Tag>
                    ))}
                  </div>
                </div>
                {totalDamagedParts > 0 && (
                  <div>
                    <Text strong>Damaged Parts ({totalDamagedParts}):</Text>
                    <div style={{ marginTop: 8 }}>
                      {(Object.values(damagedParts.selectedParts).flat() as string[]).map((part) => (
                        <Tag key={part} style={{ marginBottom: 4 }}>
                          {part}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
            {vehicle.additionalInformation && (
              <div>
                <Text strong>Additional Notes:</Text>
                <Paragraph style={{ marginTop: 4 }}>
                  {vehicle.additionalInformation}
                </Paragraph>
              </div>
            )}
          </Space>
        </Card>

        {/* Incident Details */}
        <Card 
          title={<><FileTextOutlined /> Incident Information</>} 
          style={{ marginBottom: 16 }}
        >
          <Descriptions column={1}>
            <Descriptions.Item label={<><CalendarOutlined /> Date & Time</>}>
              {incidentDetails.incidentDate ? 
                new Date(incidentDetails.incidentDate).toLocaleString() : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label={<><EnvironmentOutlined /> Location</>}>
              {incidentDetails.accidentLocationDescription || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Incident Type">
              <Tag color="blue">{incidentDetails.incidentType || 'N/A'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Weather Conditions">
              {incidentDetails.weatherConditions || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Road Conditions">
              {incidentDetails.roadConditions || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Description">
              <Paragraph>{incidentDetails.additionalSafetyNotes || 'N/A'}</Paragraph>
            </Descriptions.Item>
          </Descriptions>
          
          {(incidentDetails.witnessName || incidentDetails.witnessPhone || incidentDetails.witnessStatement) && (
            <>
              <Divider />
              <Title level={5}>Witness Information</Title>
              <Descriptions column={1}>
                {incidentDetails.witnessName && (
                  <Descriptions.Item label="Name">
                    {incidentDetails.witnessName}
                  </Descriptions.Item>
                )}
                {incidentDetails.witnessPhone && (
                  <Descriptions.Item label="Phone">
                    {incidentDetails.witnessPhone}
                  </Descriptions.Item>
                )}
                {incidentDetails.witnessStatement && (
                  <Descriptions.Item label="Statement">
                    <Paragraph>{incidentDetails.witnessStatement}</Paragraph>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </>
          )}
        </Card>

        {/* Warnings */}
        {operatorInfo.operatorInjured && (
          <Alert
            message="Injury Reported"
            description="This incident involves an injury. Additional follow-up and documentation may be required."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {!vehicle.vehicleIsDrivable && (
          <Alert
            message="Vehicle Not Drivable"
            description="The vehicle has been marked as not drivable. Towing or alternative transportation may be required."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
      </div>

      <div className="step-actions" style={{ marginTop: 40 }}>
        <Button onClick={onPrevious} disabled={submitting}>
          Previous
        </Button>
        <Button 
          type="primary" 
          icon={submitting ? <Spin /> : <SendOutlined />}
          onClick={handleSubmit}
          loading={submitting}
          size="large"
        >
          {submitting ? 'Submitting...' : 'Submit Incident Report'}
        </Button>
      </div>
    </div>
  );
};

export default ReviewSubmitStep;